import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { colors, css, fonts, glyphs, mediaQueriesNumbers, useMediaQuery } from '../../../Styles';
import { NewModal, NewTimePicker, CommitBtnStyle } from '../..'
import { ListData_ValueTypes, NewFieldTypes, SelectFieldOption_Values, TableStyles_Types, LocaleFarmDate, ValueSteps } from "../../../Definitions"
import { Graph_QL, useFeedCurves, FeedCurve, FeedCurveTime } from "../../../Services";
import { useAppSelector } from "../../../Store";
import { MaintainFeedCurve } from "../../../Services/Agrisys_GqlDataCaller/FeedDataCalls/GQLCalls/maintainFeedCurveCall";
import { cloneDeep, keyBy, merge } from "lodash";
import { convertFeedCurveToSpreadingData, convertMinutesToTime, divStyleOverride } from "../_FeedCurves/feedCurveSpreadingUtil";

// Table
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { ReactiveTable } from "../../Tables/reactiveTableRenderer";
import { GenericDataParameters, Units, useColumnConstructor } from "../../Tables/_Tables/useColumnConstructor";
import { getFarmSettingsState } from "../../../Services/Agrisys_GqlDataCaller/UserCalls/dataCalls";
import { updateSpreadingTable } from "../_FeedCurves/updateSpreadingTableHelper";
import { SHOULD_DISPLAY_CONTINUES } from "../_FeedCurves/feedCurveTableUtils";
import { NewButtonList } from "../../BaseComponents/Buttons/newButton";
import { normalizeFeedTime } from "../../BaseComponents/newTimePicker";

interface feedCurveSpreadingProps {
  onClose: () => void
  feedCurve: FeedCurve
}

function handleRemoveTime(dirtRef: MutableRefObject<MaintainFeedCurve>, timeId: number) {
  if (timeId < 0) {
    if (dirtRef.current.creates?.times) {
      dirtRef.current.creates.times = dirtRef.current.creates?.times.filter((time) => time.time_id !== timeId);
    }
    return;
  }

  if (dirtRef.current.deletes === undefined) {
    dirtRef.current.deletes = {}
  }

  if (dirtRef.current.deletes.times === undefined) {
    dirtRef.current.deletes.times = []
  }

  const deletedTimesSet = new Set(dirtRef.current.deletes.times);
  if (!deletedTimesSet.has(timeId)) {
    dirtRef.current.deletes.times.push(timeId);
  }
}

export default function FeedCurveSpreading({ feedCurve, onClose }: feedCurveSpreadingProps) {
  const { t } = useTranslation()
  const mobileXs = useMediaQuery(`(max-width: ${mediaQueriesNumbers.mobileXs}px)`);
  const farm = useAppSelector(({ activeData }) => activeData.farm)
  const connection = useMemo(() => (Graph_QL.create({ domain: farm ?? "" })), [farm]);
  const [changedTableData, setChangedTableData] = useState<{ rowId: number, columnId: string, value: string | number | boolean | SelectFieldOption_Values | undefined }>();
  const [removedTime, setRemovedTime] = useState<number | undefined>(undefined);
  const dirtRef = useRef<MaintainFeedCurve>({ feedCurveId: feedCurve.feedCurveId });

  const { maintainEntries: maintainCurveSpreading } = useFeedCurves(connection);

  const farmSettings = useAppSelector(getFarmSettingsState);

  const timezoneOffset = useMemo(() => {
    const timezoneOffset = DateTime.now().setZone(farmSettings?.time_zone_label);
    return timezoneOffset.offset - (timezoneOffset.isInDST ? 60 : 0)
  }, [farmSettings?.time_zone_label])


  const [feedTimes, setFeedTimes] = useState<Partial<FeedCurveTime>[]>(cloneDeep(feedCurve.times)?.map((time) => { return { ...time, time: LocaleFarmDate.restrictTimeTo24Hours(time.time) } })?.sort((feedCurveTimeA, feedCurveTimeB) => (feedCurveTimeA.time - feedCurveTimeB.time)) ?? []);
  const [feedPlans, setFeedPlans] = useState(() => {
    if (!feedCurve.milestones) {
      return []
    };
    return cloneDeep(feedCurve.milestones)
      .sort((milestoneA, milestoneB) => (milestoneA.milestoneIndex - milestoneB.milestoneIndex))
      .map((milestone) => {
        if (milestone.feedPlan) { milestone.feedPlan.plan.push({ day: 0, timeId: 0, amount: 0, feedPlanEntryId: 0 }) }
        return milestone.feedPlan?.plan ? [...milestone.feedPlan.plan].sort((entryA, entryB) => entryA.day - entryB.day) : [{ day: 0, timeId: 0, amount: 0, feedPlanEntryId: 0 }]
      })
  })

  const clearDirt = () => {
    dirtRef.current = { feedCurveId: feedCurve.feedCurveId }
    setFeedTimes(feedCurve.times?.map((time) => { return { ...time, time: LocaleFarmDate.restrictTimeTo24Hours(time.time) } }) ?? []);
    setFeedPlans(() => {
      if (!feedCurve.milestones) {
        return []
      };
      return cloneDeep(feedCurve.milestones)
        .sort((milestoneA, milestoneB) => (milestoneA.milestoneIndex - milestoneB.milestoneIndex))
        .map((milestone) => {
          if (milestone.feedPlan) { milestone.feedPlan.plan.push({ day: 0, timeId: 0, amount: 0, feedPlanEntryId: 0 }) }
          return milestone.feedPlan?.plan ? milestone.feedPlan.plan : [{ day: 0, timeId: 0, amount: 0, feedPlanEntryId: 0 }]
        })
    })
  }

  useEffect(() => {
    setFeedTimes(feedCurve.times?.map((time) => { return { ...time, time: LocaleFarmDate.restrictTimeTo24Hours(time.time) } }) ?? []);
    setFeedPlans(() => {
      if (!feedCurve.milestones) {
        return []
      };
      return cloneDeep(feedCurve.milestones)
        .sort((milestoneA, milestoneB) => (milestoneA.milestoneIndex - milestoneB.milestoneIndex))
        .map((milestone) => {
          if (milestone.feedPlan) { milestone.feedPlan.plan.push({ day: 0, timeId: 0, amount: 0, feedPlanEntryId: 0 }) }
          return milestone.feedPlan?.plan ? [...milestone.feedPlan.plan].sort((entryA, entryB) => entryA.day - entryB.day) : [{ day: 0, timeId: 0, amount: 0, feedPlanEntryId: 0 }]
        })
    })
  }, [feedCurve])


  const data = useMemo(() => (convertFeedCurveToSpreadingData(feedCurve, feedPlans, setFeedPlans, SHOULD_DISPLAY_CONTINUES)), [feedCurve, feedPlans])
  const tableData = useMemo(() => {
    return Array.from(data.values()).sort((a, b) => { if (a.milestoneArrayIndex === b.milestoneArrayIndex) { return a.day - b.day }; return a.milestoneArrayIndex - b.milestoneArrayIndex });
  }, [data]);

  useEffect(() => {
    if (changedTableData) {
      setChangedTableData(undefined);
      updateSpreadingTable(changedTableData.rowId, changedTableData.columnId, changedTableData.value, data, feedCurve, feedPlans, dirtRef, setFeedPlans, SHOULD_DISPLAY_CONTINUES)
    }
  }, [changedTableData, data, feedCurve, feedPlans])

  const maxDayValue = useCallback((rowId: number) => {
    const row = data.get(rowId);
    if (row === undefined) {
      return 0;
    }
    const milestoneIndex = row.milestoneIndex;
    const day = row.day;

    const following = Array.from(data.values()).filter((row) => row.day > day && row.milestoneIndex === milestoneIndex)
    const followingDays = following.map((row) => row.day);
    if (followingDays.length) {
      return Math.min(...followingDays) - 1;
    }
    return 999;
  }, [data])

  const minDayValue = useCallback((rowId: number): number => {
    const row = data.get(rowId);
    if (row === undefined) {
      return 0;
    }
    const milestoneIndex = row.milestoneIndex;
    const day = row.day;
    const previousDays = Array.from(data.values()).filter((row) => row.day < day && row.milestoneIndex === milestoneIndex).map((row) => row.day);
    if (previousDays.length) {
      const value = Math.max(...previousDays) + 1;
      return value < 0 ? 0 : value;
    }
    return 0;
  }, [data])

  const accessorData = useMemo<GenericDataParameters[]>(() => {
    const forceRerenderOnTableEdit: true = true
    return [
      // { headerValue: t("feedCurvePages.feedCurve.spreading", {defaultValue: "spreading"}), accessor: "milestoneSpreading", type: ListData_ValueTypes.Check, width: "120px" },
      { headerValue: t("milestones.general.milestone", { defaultValue: "milestone" }), accessor: "milestoneName", type: ListData_ValueTypes.Display, staticColumn: true },
      { headerValue: t("feedCurvePages.feedCurve.day", { defaultValue: "day" }), accessor: "day", type: ListData_ValueTypes.Number, width: "80px", valueStep: ValueSteps.integers, maxValueNumber: maxDayValue, minValueNumber: minDayValue, forceRerenderOnTableEdit },
      { headerValue: "", accessor: "addDay", type: ListData_ValueTypes.Button, width: "20px" },
      { headerValue: "", accessor: "removeRowActionColumn", type: ListData_ValueTypes.DeleteRowAction, width: "35px" },
      ...feedTimes.map((feedTime) => ({
        headerValue: convertMinutesToTime(feedTime.time ?? 0),
        accessor: feedTime.timeId?.toString() ?? "",
        type: ListData_ValueTypes.Mutable,
        unit: Units.percent,
        idForColor: NaN,
        forceRerenderOnTableEdit: forceRerenderOnTableEdit,
        onClick: () => { setRemovedTime(feedTime.timeId) },
        valueStep: ValueSteps.integers
        // staticColumn: tableData.some(row => row.milestoneSpreading === false) ? false : true
      })),
      { headerValue: t("units.total", { defaultValue: "total" }), accessor: "total", type: ListData_ValueTypes.Display, staticColumn: true, unit: Units.percent, divStyleOverride: divStyleOverride },
    ];
  }, [feedTimes, maxDayValue, minDayValue, t]);

  const columns = useColumnConstructor({
    accessorData: [...accessorData],
    tableStyle: TableStyles_Types.blueTable,
    canEdit: true,
  });
  const table = useReactTable({
    columns,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    state: {
      columnPinning: {
        left: mobileXs ? undefined : ["milestoneSpreading", "milestoneName", "day", "addDay", "removeRowActionColumn"],
        right: mobileXs ? undefined : ["total"]
      },
      columnOrder: ["milestoneSpreading", "milestoneName", "day", "addDay", "removeRowActionColumn", ...feedTimes.map((time) => (time.timeId?.toString() ?? "")), "nonValidAccessorValue", "total"]
    },
    meta: {
      updateData: (rowId, columnId, value) => {
        setChangedTableData({ rowId, columnId, value })
      },
      deleteRow: (deletedRowId) => {
        const oldRow = data.get(deletedRowId);
        if (!oldRow) {
          return
        }

        const deletedDays = feedPlans[oldRow.milestoneArrayIndex].filter((feedPlan) => (feedPlan.day === oldRow.day));
        const deletedDaysId = deletedDays.map((day) => day.feedPlanEntryId);
        if (dirtRef.current.creates !== undefined && dirtRef.current.creates.milestones !== undefined) {
          const milestones = dirtRef.current.creates.milestones
          const changesIndex = milestones.findIndex((milestone) => milestone.feedCurveMilestoneId === oldRow.feedCurveMilestoneId);
          if (milestones[changesIndex] && milestones[changesIndex].feedPlan) {
            milestones[changesIndex].feedPlan = milestones[changesIndex].feedPlan.filter((entry) => deletedDaysId.includes(entry.day));
            if (!milestones[changesIndex].feedPlan.length) {
              milestones.splice(changesIndex, 1);
            }
          }

        }
        if (dirtRef.current.updates !== undefined && dirtRef.current.updates.feedPlan !== undefined) {
          dirtRef.current.updates.feedPlan = dirtRef.current.updates.feedPlan.filter((entry) => !deletedDaysId.includes(entry.feedPlanEntryId));
        }

        if (dirtRef.current.deletes === undefined) {
          dirtRef.current.deletes = {}
        }

        if (dirtRef.current.deletes.feedPlan === undefined) {
          dirtRef.current.deletes.feedPlan = [];
        }
        dirtRef.current.deletes.feedPlan.push(...deletedDaysId.filter((id) => id > 0));

        feedPlans[oldRow.milestoneArrayIndex] = feedPlans[oldRow.milestoneArrayIndex].filter((feedPlan) => (feedPlan.day !== oldRow.day));
        setFeedPlans(cloneDeep(feedPlans));

      },
      allowDeleteRowCheck: (row) => { return (!row.original.milestoneName) }
    }
  });

  const reactiveTable = ReactiveTable({
    table,
    tableType: TableStyles_Types.blueTable,
    emptyText: t("feed.feedCurve.noFeedCurve", { defaultValue: "no feedCurve found" }),
    renderFooter: true,
  });


  useEffect(() => {
    if (removedTime) {
      setRemovedTime(undefined);
      for (const row of table.getRowModel().rows) {
        updateSpreadingTable(+row.original.id, String(removedTime), undefined, data, feedCurve, feedPlans, dirtRef, setFeedPlans, SHOULD_DISPLAY_CONTINUES)
      }

      handleRemoveTime(dirtRef, removedTime);
      setFeedTimes((old) => {
        return [...old].filter((time) => time.timeId !== removedTime)
      })

    }
  }, [data, feedCurve, feedPlans, removedTime, table])


  if (!feedCurve) return null;
  return (<>
    <NewModal
      header={t("feedCurvePages.feedCurve.spreading", { defaultValue: "spreading" })}
      predefinedButtons={{
        onClose: () => { clearDirt(); onClose() }
      }}
      overrides={{
        headerContentHeight: "70px"
      }}
      headerContent={
        <div style={{ display: "flex", flexDirection: "column", padding: "0px 20px", width: "100%", gap: "5px" }}>
          <NewTimePicker
            header={t("feedCurvePages.feedCurve.addTime", { defaultValue: "add time" })}
            commitBtn
            background
            overrides={{
              fieldType: NewFieldTypes.tertiaryField,
              commitBtnStyle: CommitBtnStyle.dark,
              backgroundColor: colors.blueDarkSecondary
            }}
            onTimeSelect={
              (date) => {
                if (dirtRef.current.creates === undefined) {
                  dirtRef.current.creates = {}
                }
                const newTime = normalizeFeedTime(date.getHours() * 60 + date.getMinutes());
                if (!feedTimes.map((feedTime) => feedTime?.time).includes(newTime)) {
                  dirtRef.current.creates.times = Object.values(merge({}, keyBy(dirtRef.current.creates.times, "time"), keyBy([{ time: LocaleFarmDate.restrictTimeTo24Hours(newTime - timezoneOffset), time_id: -LocaleFarmDate.restrictTimeTo24Hours(newTime - timezoneOffset) }])));
                }
                setFeedTimes((oldFeedTimes) => {
                  const copy = [...oldFeedTimes]
                  if (!oldFeedTimes.map((feedTime) => feedTime?.time).includes(newTime)) {
                    copy.push({ timeId: -LocaleFarmDate.restrictTimeTo24Hours(newTime - timezoneOffset), time: newTime })
                  }
                  return copy.sort((timeA, timeB) => (timeA.time ?? 1) - (timeB.time ?? 0));
                })
              }
            }
          />
          <p style={{...css.emptyPen, color: colors.greyLightTertiary, marginLeft: "10px"}}>Note: 00:00 is not selectable! This will default to 00.05.</p>
        </div>
      }
      secondaryContent={<div style={{ maxWidth: "1000px" }}>{reactiveTable}</div>}
      footerContent={<div style={{ marginTop: "20px" }}>
        <NewButtonList
          contents={[t("feedCurvePages.feedCurve.saveChanges", { defaultValue: "save changes" }), t("feedCurvePages.feedCurve.resetChanges", { defaultValue: "reset changes" })]}
          onClick={(i) => {
            if (i === 0) {
              if (maintainCurveSpreading) {
                maintainCurveSpreading({ data: dirtRef.current.updates }, { data: dirtRef.current.deletes }, { data: dirtRef.current.creates }, { originalFeedCurve: feedCurve, optimisticFeedCurve: feedCurve }) // TODO optimistic update
              }
              clearDirt();
              onClose();
            }
            if (i === 1) { clearDirt() }
          }}
          overrides={(index) => ({
            baseStyles: { width: "fit-content" }
          })}
        />
      </div>
      }
    />
  </>);
}
 
